import { __awaiter } from "tslib";
import { formatFilterSet } from '@/search-for-retail/filter-sets/filter-sets.formatters';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the filter set resource under a collection. */
const FILTER_SET_PATH = 'filterset';
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for filter set data.
 *
 * Filter set endpoints sit under `/collections/{collection}/filterset`.
 * Auth and tenant headers are handled by the base `ccApi`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const filterSetApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Filter set queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the list of filter sets for a collection.
         *
         * @endpoint GET /collections/{collection}/filterset
         */
        getFilterSetList: builder.query({
            query: ({ collection }) => `/collections/${collection}/${FILTER_SET_PATH}`,
            providesTags: [CC_API_TAGS.FilterSetList],
        }),
        /**
         * Fetches a single filter set by ID.
         *
         * @endpoint GET /collections/{collection}/filterset/{id}
         */
        getFilterSetById: builder.query({
            query: ({ collection, id }) => `/collections/${collection}/${FILTER_SET_PATH}/${id}`,
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.FilterSet, id },
            ],
        }),
        /**
         * Fetches usage information for a filter set.
         *
         * No `providesTags` — usage data is fetched on-demand via the lazy hook
         * and does not need automatic cache invalidation after mutations.
         *
         * @endpoint GET /collections/{collection}/filterset/{id}/usage
         */
        getFilterSetUsageById: builder.query({
            query: ({ collection, id }) => `/collections/${collection}/${FILTER_SET_PATH}/${id}/usage`,
        }),
        // -----------------------------------------------------------------------
        // Filter set mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new filter set in a collection.
         *
         * @endpoint POST /collections/{collection}/filterset
         */
        createFilterSet: builder.mutation({
            query: ({ collection, collectionId, data, filterType, }) => ({
                url: `/collections/${collection}/${FILTER_SET_PATH}`,
                method: 'POST',
                body: Object.assign(Object.assign({}, formatFilterSet(data, filterType)), { collectionId }),
            }),
            invalidatesTags: [CC_API_TAGS.FilterSetList],
        }),
        /**
         * Updates an existing filter set.
         *
         * @endpoint PUT /collections/{collection}/filterset/{id}
         */
        updateFilterSet: builder.mutation({
            query: ({ collection, data, filterType }) => ({
                url: `/collections/${collection}/${FILTER_SET_PATH}/${data.id}`,
                method: 'PUT',
                body: formatFilterSet(data, filterType),
            }),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.FilterSetList,
                { type: CC_API_TAGS.FilterSet, id: data.id },
            ],
        }),
        /**
         * Deletes a single filter set.
         *
         * @endpoint DELETE /collections/{collection}/filterset/{id}
         */
        deleteFilterSet: builder.mutation({
            query: ({ collection, id }) => ({
                url: `/collections/${collection}/${FILTER_SET_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.FilterSetList,
                { type: CC_API_TAGS.FilterSet, id },
            ],
        }),
        /**
         * Bulk-deletes filter sets (individual deletes via Promise.allSettled).
         *
         * NOTE: The backend does not provide a single bulk-delete endpoint for
         * filter sets, so each ID is deleted individually. RTK Query still
         * invalidates the list cache once the mutation settles.
         */
        bulkDeleteFilterSets: builder.mutation({
            queryFn: (_a, _api_1, _extraOptions_1, baseQuery_1) => __awaiter(void 0, [_a, _api_1, _extraOptions_1, baseQuery_1], void 0, function* ({ collection, idList }, _api, _extraOptions, baseQuery) {
                const results = yield Promise.allSettled(idList.map((id) => __awaiter(void 0, void 0, void 0, function* () {
                    const result = yield baseQuery({
                        url: `/collections/${collection}/${FILTER_SET_PATH}/${id}`,
                        method: 'DELETE',
                    });
                    if (result.error) {
                        // eslint-disable-next-line @typescript-eslint/only-throw-error
                        throw result.error;
                    }
                })));
                return { data: results };
            }),
            invalidatesTags: [CC_API_TAGS.FilterSetList],
        }),
    }),
});
export const { useGetFilterSetListQuery, useLazyGetFilterSetListQuery, useGetFilterSetByIdQuery, useGetFilterSetUsageByIdQuery, useLazyGetFilterSetUsageByIdQuery, useCreateFilterSetMutation, useUpdateFilterSetMutation, useDeleteFilterSetMutation, useBulkDeleteFilterSetsMutation, } = filterSetApi;
